import {
  forwardRef,
  useCallback,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from './cn';

/**
 * SegmentedControl — 적은 옵션(2~5)의 단일선택을 버튼 행으로 노출하는 프리미티브
 * (DESIGN-SPEC §4, §6 + frontend-design: 단일 red accent·Linear-dense).
 *
 * 상태 드롭다운(판매상태/구분/면과세/재고/진열상태 등 "전체|옵션…")을 한눈에 보이게 대체한다.
 * `ProductTableToolbar` 의 손수 만든 artist-link radiogroup 패턴(segmented border + radiogroup)
 * 을 일반화·접근성 강화한 것 — 신규 발명이 아니라 검증된 패턴의 승격.
 *
 * 선택 = Everline red **tint**(레퍼런스 Image3): `--accent-soft` 배경 + `--accent-default`
 * 텍스트(solid fill 아님, 절제된 강조). 비선택 = 뉴트럴 surface + `--content-default`.
 * 세그먼트 사이는 1px hairline. `role="radiogroup"` + `role="radio"` 로 화살표 키 이동
 * (roving tabindex)을 직접 구현(native radio 아님 — 버튼형이라 시각 제어 필요).
 */

export interface SegmentedOption<V extends string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

type SegmentSize = 'sm' | 'md';

export interface SegmentedControlProps<V extends string> {
  value: V;
  onChange: (value: V) => void;
  options: ReadonlyArray<SegmentedOption<V>>;
  /** 컨트롤 높이. sm=28px(필터바·인라인), md=32px(폼·기본). 기본 md. */
  size?: SegmentSize;
  /** 그룹 전체 비활성. */
  disabled?: boolean;
  /** 컨테이너를 부모 폭만큼 늘리고 세그먼트를 균등 분할. 기본 false(콘텐츠 폭). */
  fullWidth?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
  'data-testid'?: string;
}

const SIZE_HEIGHT: Record<SegmentSize, string> = {
  sm: 'var(--control-h-sm)',
  md: 'var(--control-h-md)',
};

const SIZE_CLASS: Record<SegmentSize, string> = {
  sm: 'px-2.5 text-[12px]',
  md: 'px-3 text-[13px]',
};

// 제네릭 + forwardRef 동거: 캐스트로 제네릭 시그니처 보존.
const SegmentedControlInner = <V extends string>(
  {
    value,
    onChange,
    options,
    size = 'md',
    disabled = false,
    fullWidth = false,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    className,
    'data-testid': testId,
  }: SegmentedControlProps<V>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const enabledIndexes = options.flatMap((o, i) => (o.disabled ? [] : [i]));

  const focusIndex = useCallback((idx: number) => {
    const el = btnRefs.current[idx];
    el?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (disabled || enabledIndexes.length === 0) return;
      const currentEnabledPos = enabledIndexes.findIndex(
        (i) => options[i]?.value === value,
      );
      let nextPos = currentEnabledPos;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextPos = (currentEnabledPos + 1) % enabledIndexes.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextPos =
          (currentEnabledPos - 1 + enabledIndexes.length) % enabledIndexes.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextPos = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextPos = enabledIndexes.length - 1;
      } else {
        return;
      }
      const nextIdx = enabledIndexes[nextPos];
      if (nextIdx === undefined) return;
      const nextOpt = options[nextIdx];
      if (!nextOpt) return;
      onChange(nextOpt.value);
      focusIndex(nextIdx);
    },
    [disabled, enabledIndexes, focusIndex, onChange, options, value],
  );

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      data-testid={testId}
      onKeyDown={handleKeyDown}
      className={cn(
        'inline-flex overflow-hidden rounded-md border',
        fullWidth && 'flex w-full',
        disabled && 'opacity-50',
        className,
      )}
      style={{ borderColor: 'var(--divider-strong)', background: 'var(--surface-0)' }}
    >
      {options.map((opt, i) => {
        const active = opt.value === value;
        const isDisabled = disabled || opt.disabled;
        // roving tabindex: 선택된(또는 첫 활성) 세그먼트만 탭 진입점.
        const isTabStop = active || (value === undefined && i === enabledIndexes[0]);
        return (
          <button
            key={opt.value}
            ref={(el) => {
              btnRefs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={isDisabled}
            tabIndex={isTabStop ? 0 : -1}
            onClick={() => {
              if (!isDisabled) onChange(opt.value);
            }}
            className={cn(
              'inline-flex items-center justify-center font-medium transition-colors',
              'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]',
              'disabled:cursor-not-allowed disabled:opacity-50',
              SIZE_CLASS[size],
              fullWidth && 'flex-1',
            )}
            style={{
              height: SIZE_HEIGHT[size],
              // 레퍼런스 Image3: 선택 = red tint(accent-soft) + red 텍스트(절제), solid fill 아님.
              background: active ? 'var(--accent-soft)' : 'transparent',
              color: active ? 'var(--accent-default)' : 'var(--content-default)',
              fontWeight: active ? 600 : 500,
              borderLeft: i === 0 ? 'none' : '1px solid var(--divider-strong)',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

// 제네릭 보존 캐스트(forwardRef 가 제네릭을 지움 → 호출부에서 V 추론되도록 재타이핑).
export const SegmentedControl = forwardRef(SegmentedControlInner) as <V extends string>(
  props: SegmentedControlProps<V> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof SegmentedControlInner>;
